/**
 * Custom Email Service for better deliverability
 * Can be integrated with SendGrid, Mailgun, AWS SES, etc.
 */

export interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

export interface EmailOptions {
  to: string;
  template: EmailTemplate;
  data?: Record<string, any>;
}

class EmailService {
  private static instance: EmailService;
  private apiKey: string;
  private fromEmail: string;
  private fromName: string;
  private baseUrl: string;

  private constructor() {
    // These would come from environment variables
    this.apiKey = import.meta.env.VITE_EMAIL_API_KEY || '';
    this.fromEmail = import.meta.env.VITE_FROM_EMAIL || 'noreply@yourdomain.com';
    this.fromName = import.meta.env.VITE_FROM_NAME || 'P2P Exchange';
    this.baseUrl = import.meta.env.VITE_EMAIL_API_URL || '/api/send-email';
  }

  static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  /**
   * Send password reset email with custom template
   */
  async sendPasswordResetEmail(email: string, resetLink: string): Promise<void> {
    const template = this.getPasswordResetTemplate(resetLink);
    
    const emailOptions: EmailOptions = {
      to: email,
      template,
      data: {
        resetLink,
        appName: 'P2P Exchange',
        supportEmail: 'support@yourdomain.com'
      }
    };

    try {
      await this.sendEmail(emailOptions);
      console.log('✅ Password reset email sent successfully');
    } catch (error) {
      console.error('❌ Failed to send password reset email:', error);
      throw new Error('Failed to send password reset email');
    }
  }

  /**
   * Send welcome email after registration
   */
  async sendWelcomeEmail(email: string, displayName?: string): Promise<void> {
    const template = this.getWelcomeTemplate(displayName);
    
    const emailOptions: EmailOptions = {
      to: email,
      template,
      data: {
        displayName: displayName || 'User',
        appName: 'P2P Exchange',
        supportEmail: 'support@yourdomain.com'
      }
    };

    try {
      await this.sendEmail(emailOptions);
      console.log('✅ Welcome email sent successfully');
    } catch (error) {
      console.error('❌ Failed to send welcome email:', error);
      // Don't throw error for welcome email - it's not critical
    }
  }

  /**
   * Send email verification
   */
  async sendVerificationEmail(email: string, verificationLink: string): Promise<void> {
    const template = this.getVerificationTemplate(verificationLink);
    
    const emailOptions: EmailOptions = {
      to: email,
      template,
      data: {
        verificationLink,
        appName: 'P2P Exchange',
        supportEmail: 'support@yourdomain.com'
      }
    };

    try {
      await this.sendEmail(emailOptions);
      console.log('✅ Verification email sent successfully');
    } catch (error) {
      console.error('❌ Failed to send verification email:', error);
      throw new Error('Failed to send verification email');
    }
  }

  /**
   * Generic email sending method
   */
  private async sendEmail(options: EmailOptions): Promise<void> {
    if (!this.apiKey) {
      console.warn('⚠️ Email service not configured, skipping email send');
      return;
    }

    const payload = {
      from: {
        email: this.fromEmail,
        name: this.fromName
      },
      to: options.to,
      subject: options.template.subject,
      html: options.template.html,
      text: options.template.text
    };

    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Email service error: ${error.error || error.message || 'Unknown error'}`);
    }

    const result = await response.json();
    console.log('📧 Email sent successfully:', result);
  }

  /**
   * Password reset email template
   */
  private getPasswordResetTemplate(resetLink: string): EmailTemplate {
    return {
      subject: 'Reset Your P2P Exchange Password',
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Reset Your Password</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #1976d2; margin: 0;">P2P Exchange</h1>
              <p style="color: #666; margin: 5px 0 0 0;">Currency Exchange Platform</p>
            </div>
            
            <div style="margin-bottom: 30px;">
              <h2 style="color: #333; margin-bottom: 20px;">Password Reset Request</h2>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                Hello,
              </p>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                You requested to reset your password for your P2P Exchange account. Click the button below to reset your password:
              </p>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${resetLink}" 
                   style="background-color: #1976d2; color: #ffffff; padding: 12px 30px; text-decoration: none; border-radius: 4px; display: inline-block; font-weight: bold;">
                  Reset Password
                </a>
              </div>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                If the button doesn't work, copy and paste this link into your browser:
              </p>
              <p style="color: #1976d2; word-break: break-all; font-size: 14px;">
                ${resetLink}
              </p>
            </div>
            
            <div style="border-top: 1px solid #eee; padding-top: 20px; margin-top: 30px;">
              <p style="color: #999; font-size: 14px; margin-bottom: 10px;">
                If you didn't request this password reset, please ignore this email. Your password will remain unchanged.
              </p>
              <p style="color: #999; font-size: 14px; margin: 0;">
                This link will expire in 1 hour for security reasons.
              </p>
            </div>
            
            <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
              <p style="color: #999; font-size: 12px; margin: 0;">
                Best regards,<br>
                P2P Exchange Team
              </p>
            </div>
          </div>
        </body>
        </html>
      `,
      text: `
        P2P Exchange - Password Reset Request
        
        Hello,
        
        You requested to reset your password for your P2P Exchange account.
        
        Click this link to reset your password:
        ${resetLink}
        
        If you didn't request this password reset, please ignore this email.
        
        This link will expire in 1 hour for security reasons.
        
        Best regards,
        P2P Exchange Team
      `
    };
  }

  /**
   * Welcome email template
   */
  private getWelcomeTemplate(displayName?: string): EmailTemplate {
    const name = displayName || 'User';
    
    return {
      subject: 'Welcome to P2P Exchange!',
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Welcome to P2P Exchange</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #1976d2; margin: 0;">P2P Exchange</h1>
              <p style="color: #666; margin: 5px 0 0 0;">Currency Exchange Platform</p>
            </div>
            
            <div style="margin-bottom: 30px;">
              <h2 style="color: #333; margin-bottom: 20px;">Welcome to P2P Exchange!</h2>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                Hello ${name},
              </p>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                Thank you for joining P2P Exchange! You now have access to real-time currency exchange rates, 
                secure transactions, and a comprehensive trading platform.
              </p>
              
              <div style="background-color: #f8f9fa; padding: 20px; border-radius: 4px; margin: 20px 0;">
                <h3 style="color: #333; margin-top: 0;">What you can do now:</h3>
                <ul style="color: #666; line-height: 1.6;">
                  <li>View real-time exchange rates</li>
                  <li>Set up your favorite currencies</li>
                  <li>Track conversion history</li>
                  <li>Access secure trading features</li>
                </ul>
              </div>
            </div>
            
            <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
              <p style="color: #999; font-size: 12px; margin: 0;">
                Best regards,<br>
                P2P Exchange Team
              </p>
            </div>
          </div>
        </body>
        </html>
      `,
      text: `
        P2P Exchange - Welcome!
        
        Hello ${name},
        
        Thank you for joining P2P Exchange! You now have access to real-time currency exchange rates, 
        secure transactions, and a comprehensive trading platform.
        
        What you can do now:
        - View real-time exchange rates
        - Set up your favorite currencies
        - Track conversion history
        - Access secure trading features
        
        Best regards,
        P2P Exchange Team
      `
    };
  }

  /**
   * Email verification template
   */
  private getVerificationTemplate(verificationLink: string): EmailTemplate {
    return {
      subject: 'Verify Your P2P Exchange Email',
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Verify Your Email</title>
        </head>
        <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f5f5f5;">
          <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
              <h1 style="color: #1976d2; margin: 0;">P2P Exchange</h1>
              <p style="color: #666; margin: 5px 0 0 0;">Currency Exchange Platform</p>
            </div>
            
            <div style="margin-bottom: 30px;">
              <h2 style="color: #333; margin-bottom: 20px;">Verify Your Email Address</h2>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                Hello,
              </p>
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                Please verify your email address to complete your P2P Exchange account setup.
              </p>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${verificationLink}" 
                   style="background-color: #1976d2; color: #ffffff; padding: 12px 30px; text-decoration: none; border-radius: 4px; display: inline-block; font-weight: bold;">
                  Verify Email
                </a>
              </div>
              
              <p style="color: #666; line-height: 1.6; margin-bottom: 20px;">
                If the button doesn't work, copy and paste this link into your browser:
              </p>
              <p style="color: #1976d2; word-break: break-all; font-size: 14px;">
                ${verificationLink}
              </p>
            </div>
            
            <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
              <p style="color: #999; font-size: 12px; margin: 0;">
                Best regards,<br>
                P2P Exchange Team
              </p>
            </div>
          </div>
        </body>
        </html>
      `,
      text: `
        P2P Exchange - Verify Your Email
        
        Hello,
        
        Please verify your email address to complete your P2P Exchange account setup.
        
        Click this link to verify your email:
        ${verificationLink}
        
        Best regards,
        P2P Exchange Team
      `
    };
  }
}

export default EmailService;
